import React, { useEffect, useState } from 'react';
import { BanknotesIcon, CalendarIcon, ChevronLeftIcon, CreditCardIcon, PrinterIcon } from '@heroicons/react/16/solid';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { AppUser } from '../../../../models/AppUser';
import { useAppUser } from '../../../../hooks/useAppUser';
import showErrorToast from '../../../../components/Toasts/ShowErrorToast';
import { AppUserService } from '../../../../services/appUserService';
import showSuccessToast from '../../../../components/Toasts/ShowSuccessToast';
import Loading from '../../../../components/Loading';
import { Avatar } from '../../../../components/Avatar';
import { Heading, Subheading } from '../../../../components/Heading';
import { Badge } from '../../../../components/Badge';
import dateFormat from 'dateformat';
import { Button } from '../../../../components/Button';
import { DescriptionDetails, DescriptionList, DescriptionTerm } from '../../../../components/Description-List';
import { Card } from '../../../../components/Card';
import { Dialog, DialogActions, DialogDescription, DialogTitle } from '../../../../components/Dialog';
import { AppTrackLocation } from '../../../../models/AppTrackLocation';
import { useAppTrackLocations } from '../../../../hooks/useAppTrackLocation';
import { useAppPreCallPlans } from '../../../../hooks/useAppPreCallPlan';
import { PreCallPlan } from '../../../../models/AppPreCallPlan';
import { PostCallPlan } from '../../../../models/AppPostCallPlan';
import { useAppPostCallPlans } from '../../../../hooks/useAppPostCallPlan';
import { generatePostCallPlanPDF, printPDF } from '../../../../utils/pdfUtils';

const ViewAppPostCallPlanIdPage: React.FC = () => {
    const { id } = useParams<{ id: string }>();
    const [plan, setPlan] = useState<PostCallPlan | null>(null);
    const navigate = useNavigate();
    const { fetchPostCallPlanById, loading, error, } = useAppPostCallPlans();

    useEffect(() => {
        document.title = "View App Post Call Plan";
    }, []);

    useEffect(() => {
        if (!id) return;

        const fetchPlanDetails = async () => {
            try {
                const fetchedPlan = await fetchPostCallPlanById(Number(id));
                setPlan(fetchedPlan!);
            } catch (err) {
                console.error('Failed to fetch post call plan details:', err);
            }
        };

        fetchPlanDetails();
    }, [id]);

    const handlePrintReport = () => {
        if (!plan) {
            showErrorToast('No plan data available for printing');
            return;
        }

        try {
            const doc = generatePostCallPlanPDF(plan);
            const filename = `PostCallPlan_${plan.id}_${new Date().toISOString().split('T')[0]}.pdf`;
            printPDF(doc, filename);
            showSuccessToast('PDF report generated successfully');
        } catch (error) {
            console.error('Error generating PDF:', error);
            showErrorToast('Failed to generate PDF report');
        }
    };

    if (loading) return <Loading />;
    if (error) return <p className="py-16 text-center text-red-700">{error}</p>;
    if (!plan) return <p className="py-16 text-center">Post Call Plan not found.</p>;

    return (
        <>
            <div className="max-lg:hidden">
                <Link to="/gp/app/postcallplans" className="inline-flex items-center gap-2 text-sm/6 text-zinc-500 dark:text-zinc-400">
                    <ChevronLeftIcon className="size-4 fill-zinc-400 dark:fill-zinc-500" />
                    Post Call Plans
                </Link>
            </div>
            <div className="mt-4 lg:mt-8">
                <div className="flex flex-wrap items-center justify-between gap-3">
                    <div className="flex items-center gap-4">
                        <Avatar square initials={plan.pre_call_plan_id.toString()} className="bg-zinc-300 text-gray-700 size-8" />
                        <Heading>{plan.pre_call_plan_id}</Heading>
                    </div>
                    <Button onClick={handlePrintReport} className="flex items-center gap-2">
                        <PrinterIcon className="h-4 w-4" />
                        Print Report
                    </Button>
                </div>
                <div className="isolate mt-2.5 flex flex-wrap justify-between gap-x-6 gap-y-4">
                    <div className="flex flex-wrap gap-x-10 gap-y-4 py-1.5">
                        <span className="flex items-center gap-2 text-base/6 text-zinc-950 sm:text-sm/6 dark:text-white">
                            <CalendarIcon className="size-4 shrink-0 fill-zinc-400 dark:fill-zinc-500" />
                            <span>{dateFormat(plan.created_time, "mmmm dd, yyyy")}</span>
                        </span>
                    </div>
                </div>
            </div>

            <Card className="mt-12 p-6">
                <Subheading>Post Call Plan</Subheading>
                <DescriptionList className="mt-4">
                    <DescriptionTerm>Pre Call Plans</DescriptionTerm>
                    <DescriptionDetails>{plan.pre_call_plan_id || '-'}</DescriptionDetails>
                    <DescriptionTerm>Planned Date</DescriptionTerm>
                    <DescriptionDetails>
                        {plan.planned_date
                            ? dateFormat(plan.planned_date, "mmmm dd, yyyy")
                            : 'Not set'
                        }
                    </DescriptionDetails>
                    <DescriptionTerm>Salesman</DescriptionTerm>
                    <DescriptionDetails>{plan.salesman_username || '-'}</DescriptionDetails>
                    <DescriptionTerm>Product</DescriptionTerm>
                    <DescriptionDetails>{plan.product_description || '-'}</DescriptionDetails>
                    <DescriptionTerm>Customer</DescriptionTerm>
                    <DescriptionDetails>{plan.customer_fullname || '-'}</DescriptionDetails>
                    <DescriptionTerm>Remarks</DescriptionTerm>
                    <DescriptionDetails>{plan.remarks || '-'}</DescriptionDetails>
                </DescriptionList>
            </Card>
        </>
    );
};

export default ViewAppPostCallPlanIdPage;
